import type {
  DehydratedViewportsConfigurations,
  StudyGroup,
} from '../ViewportsConfigurations/types';
import omit from 'ramda/es/omit';
import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { useSplitFlag, FF } from 'modules/feature-flags';
import {
  isAtLeastPhase4,
  isPhase4Steps,
} from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/constants';
import { useStudies, useBaseViewerData } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { usePreviewHangingProtocolConfiguration } from './usePreviewHangingProtocol';
import type { GetBaseViewerDataQuery } from 'generated/graphql';
import { dehydratedHangingProtocol } from 'domains/viewer/ViewportsConfigurations';
import type { ViewportsConfigurations } from 'domains/viewer/ViewportsConfigurations';
import type { Layouts } from '../Viewer/StudyLoader/viewerLoaderState';
import { layoutsForAllCasesState } from '../Viewer/StudyLoader/viewerLoaderState';
import {
  useViewportsConfigurationsSelector,
  hangingProtocolLayoutsState,
  activeHangingProtocolSmidState,
} from '../ViewportsConfigurations/state';
import { ViewType } from 'generated/graphql';
import { logger } from 'modules/logger';
import { hydrateViewportsConfigurations } from '../ViewportsConfigurations/manipulators';
import { WithApolloContext } from 'modules/Apollo/types';
import {
  hangingProtocolStepIndex,
  hangingProtocolSubStepIndex,
  steppedHPConfigurations,
  clinicalStepsBaseline,
  getStepsFromVersioned,
  makeVersionedSteps,
} from './state';
import { stepFromGqlFormat } from './steppedHPTypes';
import { rebuildHPFromStep } from './stepUtils';
import type { HangingProtocol } from './stepUtils';
import { hpInvalidationChannel } from './broadcastHPInvalidation';
import { useViewerId } from 'hooks/useViewerId';
import { useCurrentCaseId } from 'hooks/useCurrentCase';

// Re-export for backward compatibility with existing consumers
export { broadcastHPInvalidation } from './broadcastHPInvalidation';
// Re-export for backward compatibility with existing consumers
export type { HangingProtocol } from './stepUtils';
export { rebuildHPFromStep } from './stepUtils';

// Module-level variable to track analytics across all hook instances.
// This prevents duplicate analytics events when multiple components call useHangingProtocol().
// The key combines hanging protocol smid and first study smid to ensure analytics fire
// when the same protocol is applied to different cases.
// Exported for testing purposes only.
export let __lastTrackedHangingProtocolKey: string | null = null;

// Reset function for tests
export const __resetLastTrackedHangingProtocolKey = (): void => {
  __lastTrackedHangingProtocolKey = null;
};

// Module-level guard tracking the last (case + HP smid) combination whose steps were initialized.
// Keyed by BOTH case and HP smid because the same HP smid can match different cases: keying on the
// HP smid alone let a navigation to a new case matching the same HP skip re-init and keep stale
// step state (VX-4429). Must be module-level (not a ref) because the component can unmount/remount
// during step navigation, which would reset a ref and allow the init effect to re-run, overwriting
// user edits (e.g. renames) with the original GQL data.
let __lastInitializedHPKey: string | null = null;

// Reset function for tests
export const __resetLastInitializedHPKey = (): void => {
  __lastInitializedHPKey = null;
};

/**
 * Reset the HP initialization guard so the next refetch re-processes the HP
 * data even if the key hasn't changed (e.g. after a create/update mutation).
 */
export const invalidateHPInitializationCache = (): void => {
  __lastInitializedHPKey = null;
};

export type UseHangingProtocol = {
  loadingHangingProtocol: boolean;
  hangingProtocol: Nullable<HangingProtocol>;
  hangingProtocolViewportsConfigurations: Nullable<ViewportsConfigurations>;
  hangingProtocolLayouts: Nullable<Layouts>;
  resetToHP: () => void;
};

const useMatchedHangingProtocol = ({
  groupedStudies,
  loading,
  studyDescriptions,
  cases,
  context,
}: {
  cases: Array<Array<string>>;
  groupedStudies: Array<StudyGroup>;
  loading: boolean;
  studyDescriptions: Array<string>;
} & WithApolloContext): [
  GetBaseViewerDataQuery['matchHangingProtocolByWorklistsAndStudies'],
  boolean,
] => {
  const { hangingProtocol, loading: loadingHangingProtocol } = useBaseViewerData({ context });
  return [hangingProtocol, loadingHangingProtocol];
};

export const useHangingProtocol = ({ context }: WithApolloContext): UseHangingProtocol => {
  const viewerId = useViewerId();
  const isPrimaryWindow = viewerId === '0';
  const currentCaseId = useCurrentCaseId();
  const previewedHangingProtocol = usePreviewHangingProtocolConfiguration();
  const { loading, groupedStudies, refetch, invalidateAllPrecachedStudies } = useStudies({
    context,
  });
  const [phase] = useSplitFlag(FF.HANGING_PROTOCOL_PHASE);
  const isPhase4 = isAtLeastPhase4(phase);
  // Gate all Stepped-HP behavior on the phase-4-steps treatment. The step UI (StepBar,
  // useClinicalStepMode) is already gated on this phase; the hook must gate too,
  // otherwise a phase-4-steps-off user opening an HP that has step data would still get
  // the viewer rebuilt from step 0 (Split treatments are per-user/clinic, so
  // stepped HPs can exist in the DB while a given user's phase is not phase-4-steps).
  const isHPStepsEnabled = isPhase4Steps(phase);
  const [stepIndex, setStepIndex] = useRecoilState(hangingProtocolStepIndex);

  // Stepped HP state - will be used when backend returns steps format
  const setSteppedHPConfigurations = useSetRecoilState(steppedHPConfigurations);
  const liveStepConfigurationsVersioned = useRecoilValue(steppedHPConfigurations);
  const liveStepConfigurations = getStepsFromVersioned(liveStepConfigurationsVersioned);
  const setSubStepIndex = useSetRecoilState(hangingProtocolSubStepIndex);
  const setClinicalBaseline = useSetRecoilState(clinicalStepsBaseline);

  const studyDescriptions: string[] = useMemo(
    () =>
      groupedStudies.flatMap((studies) =>
        studies.reduce<string[]>(
          (studies, { description }) => (description != null ? [...studies, description] : studies),
          []
        )
      ),
    [groupedStudies]
  );
  const cases = useMemo(() => {
    return groupedStudies.map((group) => group.map((study) => study.smid));
  }, [groupedStudies]);

  const [matchedHangingProtocol, matchedHangingProtocolLoading] = useMatchedHangingProtocol({
    groupedStudies,
    loading,
    studyDescriptions,
    cases,
    context,
  });

  const originalHangingProtocol = useMemo(
    () => previewedHangingProtocol ?? matchedHangingProtocol,
    [matchedHangingProtocol, previewedHangingProtocol]
  );

  useEffect(() => {
    // Feature-flag gate: with Stepped HP off, never process step data so the
    // viewer atoms (steppedHPConfigurations, stepIndex) stay at their flat-HP
    // defaults and production behavior is unchanged. Clear the init guard so
    // steps re-initialize cleanly if the flag flips on mid-session (Split cohort
    // change without a reload); otherwise a stale smid in the guard would make
    // the later flag-on pass skip initialization.
    if (!isHPStepsEnabled) {
      __lastInitializedHPKey = null;
      return;
    }

    const hpSmid = originalHangingProtocol?.smid ?? null;
    // Key the guard on case + HP smid. The same HP smid can match different cases, so keying on the
    // smid alone would skip re-init when navigating to a new case that matches the same HP, leaving
    // stale step state and a possibly out-of-bounds stepIndex (VX-4429). The '__no_hp__' segment
    // also serves as the null sentinel so a deleted/absent HP still produces a stable key.
    const hpKey = `${currentCaseId ?? '__no_case__'}:${hpSmid ?? '__no_hp__'}`;

    // Only initialize when this exact case+HP changes, not on every re-render. This prevents
    // overwriting user edits (e.g. renames) with the original GQL data.
    if (hpSmid != null && hpKey === __lastInitializedHPKey) {
      return;
    }
    // Skip re-initialization when the HP is temporarily null (e.g. during a
    // refetch cycle). Without this guard, a refetch that briefly returns null
    // would clear steppedHPConfigurations then re-populate them, causing a
    // flash of empty step chips in dual-window mode.
    if (hpSmid == null && __lastInitializedHPKey != null) {
      return;
    }
    __lastInitializedHPKey = hpKey;

    // Convert GQL array-format steps to internal Record-based format.
    // Treat empty arrays as null so flat HPs don't present as stepped.
    const rawStepConfigs = (originalHangingProtocol?.steps ?? []).map(stepFromGqlFormat);
    const stepConfigs = rawStepConfigs.length > 0 ? rawStepConfigs : null;

    setSteppedHPConfigurations((prev) =>
      makeVersionedSteps(stepConfigs, prev, originalHangingProtocol?.name ?? null)
    );
    setClinicalBaseline(stepConfigs ? structuredClone(stepConfigs) : null);

    // Every HP change (case transition, preview accept, etc.) should land on
    // the first step. Without this, leftover stepIndex from the previous HP
    // survives — e.g., navigating from an 8-step MR on step 4 to a 10-step
    // MR keeps you on step 4 instead of showing step 0 as active.
    setStepIndex(0);
    setSubStepIndex(0);
  }, [
    isHPStepsEnabled,
    originalHangingProtocol,
    currentCaseId,
    setStepIndex,
    setSteppedHPConfigurations,
    setClinicalBaseline,
    setSubStepIndex,
  ]);

  // Listen for cross-window HP invalidation signals. When viewer/0 saves or
  // deletes an HP, it broadcasts so viewer/1 can refetch and re-initialize.
  const handleHPInvalidation = useCallback(async () => {
    invalidateHPInitializationCache();
    await invalidateAllPrecachedStudies();
    await refetch();
  }, [invalidateAllPrecachedStudies, refetch]);

  useEffect(() => {
    const handler = () => {
      // handleHPInvalidation is async; catch so a rejected refetch doesn't surface
      // as an unhandled promise rejection from the BroadcastChannel callback.
      handleHPInvalidation().catch((error) => {
        logger.warn('Failed to handle hanging-protocol invalidation broadcast', { error });
      });
    };
    // Use addEventListener (not onmessage) so multiple concurrent useHangingProtocol instances
    // (HangingProtocolWidget, useLayouts, viewport overlays) each register their own handler.
    // Assigning the single onmessage slot in each instance clobbered the others' handlers, so
    // cross-window HP invalidation could be silently dropped (VX-4430).
    hpInvalidationChannel.addEventListener('message', handler);
    return () => {
      hpInvalidationChannel.removeEventListener('message', handler);
    };
  }, [handleHPInvalidation]);

  // For stepped HPs, the GQL response has empty top-level data.layouts and
  // viewportsConfigurations (they live per-step instead). This memo replaces
  // those fields with the current step's values so downstream consumers
  // (getHangingProtocolLayouts, dehydrateHangingProtocol, etc.) work unchanged.
  // Uses liveStepConfigurations (Recoil atom) so renames and edits are reflected.
  const hangingProtocol = useMemo(() => {
    // Feature-flag gate (critical): with Stepped HP off, always return the
    // original (flat) HP so the viewer layout/viewports are never rebuilt from
    // step data. This is the definitive production-safety guard -- everything
    // downstream (dehydrateHangingProtocol, getHangingProtocolLayouts, the
    // dehydratedHangingProtocol atom write) derives from this value.
    if (!isHPStepsEnabled) {
      return originalHangingProtocol;
    }

    const stepConfigs = liveStepConfigurations;
    if (stepConfigs == null || stepConfigs.length === 0 || stepIndex >= stepConfigs.length) {
      return originalHangingProtocol;
    }

    // Guard against a cross-window step echo (steppedHPConfigurations syncs via
    // BroadcastChannel) arriving before this window's own HP query resolves.
    // Rebuilding from a null HP would produce an smid-less object and transiently
    // null activeHangingProtocolSmidState.
    if (originalHangingProtocol == null) {
      return originalHangingProtocol;
    }

    const stepConfig = stepConfigs[stepIndex];
    const rebuilt = rebuildHPFromStep(originalHangingProtocol, stepConfig);

    return rebuilt;
  }, [isHPStepsEnabled, originalHangingProtocol, liveStepConfigurations, stepIndex]);

  const hangingProtocolViewportsConfigurations = useMemo(() => {
    return dehydrateHangingProtocol(hangingProtocol);
  }, [hangingProtocol]);

  const setHangingProtocol = useRecoilCallback(
    ({ set }) =>
      (caseSmid: Nullable<string>, value: Nullable<DehydratedViewportsConfigurations>) => {
        if (caseSmid == null) return;
        set(dehydratedHangingProtocol(caseSmid), value);
      },
    []
  );
  const setHangingProtocolSmid = useSetRecoilState(activeHangingProtocolSmidState);
  useEffect(() => {
    // The HP widget only renders on the primary window (viewer/0). Only the primary
    // window should write to broadcast atoms to avoid infinite ping-pong between windows.
    // The secondary window receives values via broadcastChannelSynchronizerEffect.
    if (!isPrimaryWindow) return;
    // Note: This *should* be fine. Null is the default, but should not occur again after the initial query. Putting this gate fixes tricky issues with TestViewportWrapper.
    if (hangingProtocolViewportsConfigurations == null) return;

    setHangingProtocol(currentCaseId, hangingProtocolViewportsConfigurations);
  }, [isPrimaryWindow, currentCaseId, setHangingProtocol, hangingProtocolViewportsConfigurations]);

  // Set the HP smid independently of the dehydrated viewport configs.
  // The smid is available from hangingProtocol immediately, but viewport
  // configs may be delayed (async step rebuild, hydration). Gating the
  // smid on viewport configs caused createStepOnBackend and saveStepOrder
  // to silently skip backend calls (they guard on hpSmid being non-null).
  //
  // Like the effect above, only the primary window should write to this
  // broadcast atom — secondary windows consume it via
  // broadcastChannelSynchronizerEffect. Writing from both windows increases
  // cross-window chatter and can reintroduce multi-window race conditions.
  useEffect(() => {
    if (!isPrimaryWindow) return;
    setHangingProtocolSmid(hangingProtocol?.smid ?? null);
  }, [isPrimaryWindow, setHangingProtocolSmid, hangingProtocol?.smid]);

  useEffect(() => {
    const hpSmid = hangingProtocol?.smid ?? null;
    const primaryStudySmid = groupedStudies[0]?.[0]?.smid ?? null;
    const trackingKey =
      primaryStudySmid != null && hpSmid != null ? `${primaryStudySmid}:${hpSmid}` : null;

    if (
      !isPhase4 ||
      trackingKey == null ||
      trackingKey === __lastTrackedHangingProtocolKey ||
      hangingProtocol == null
    ) {
      return;
    }

    const studies = groupedStudies.flat().map((study) => ({
      smid: study.smid,
      description: study.description ?? null,
      series_descriptions: study.seriesList?.map((s) => s.description).filter(Boolean) ?? [],
    }));

    analytics.track(viewer.usr.hangingProtocolApplied, {
      hanging_protocol_name: hangingProtocol.name ?? null,
      hanging_protocol_smid: hpSmid,
      has_owner: true,
      hanging_protocol_shape: {
        name: hangingProtocol.name ?? '',
        study_descriptions: hangingProtocol.studyDescriptions ?? [],
        number_of_priors: hangingProtocol.numberOfPriors ?? 0,
        study_criteria: hangingProtocol.studyCriteria ?? null,
        layouts: hangingProtocol.data?.layouts ?? [],
        viewports_configurations:
          hangingProtocol.viewportsConfigurations?.map((vc) => ({
            id: vc.id,
            view_type: vc.configuration.viewType,
            series_descriptions: vc.configuration.seriesDescriptions ?? [],
            series_criteria: vc.configuration.seriesCriteria ?? null,
            comparison_index: vc.configuration.comparisonIndex ?? null,
            matched_study_smid: vc.configuration.study?.smid ?? null,
            matched_series_smid: vc.configuration.series?.smid ?? null,
          })) ?? [],
      },
      studies,
    });

    __lastTrackedHangingProtocolKey = trackingKey;
  }, [hangingProtocol, isPhase4, groupedStudies]);

  const hangingProtocolLayouts: Nullable<Layouts> = useMemo(() => {
    return getHangingProtocolLayouts(hangingProtocol);
  }, [hangingProtocol]);

  const setHangingProtocolLayouts = useSetRecoilState(hangingProtocolLayoutsState);
  useEffect(() => {
    setHangingProtocolLayouts(hangingProtocolLayouts);
  }, [setHangingProtocolLayouts, hangingProtocolLayouts]);

  const viewportsConfigurationsRecoilSelector = useViewportsConfigurationsSelector({ context });
  const resetToHP = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        // the selector calls set on the base atom, so reset is safe here
        reset(viewportsConfigurationsRecoilSelector);
        // Prefer set() over reset() as a defensive measure: reset() on atoms
        // with selector-based defaults produces a DefaultValue instance, which
        // broadcastChannelSynchronizerEffect silently skips. layoutsForAllCasesState
        // has a concrete default so reset() would work today, but set() is
        // resilient to future changes in the atom's default definition.
        // Scope the clear to the current case; layoutsForAllCasesState holds
        // every case's layout overrides, so clearing the whole map would wipe
        // out other cases' layouts too.
        set(layoutsForAllCasesState, (prev) =>
          currentCaseId != null ? omit([currentCaseId], prev) : prev
        );
      },
    [viewportsConfigurationsRecoilSelector, currentCaseId]
  );

  return {
    hangingProtocol,
    loadingHangingProtocol: matchedHangingProtocolLoading,
    hangingProtocolViewportsConfigurations:
      hangingProtocolViewportsConfigurations != null
        ? hydrateViewportsConfigurations(hangingProtocolViewportsConfigurations, groupedStudies)
        : undefined,
    hangingProtocolLayouts,
    resetToHP,
  };
};

function validateViewType(viewType?: ViewType | null): ViewType {
  // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'ViewType | null | undefined' is not assignable to parameter of type 'ViewType'.
  if (!Object.values(ViewType).includes(viewType)) {
    logger.debug(`Invalid ViewType from Hanging Protocol viewType: ${viewType ?? 'null'}`);
  }
  return viewType ?? ViewType.TwoDDre;
}

export function dehydrateHangingProtocol(
  hangingProtocol: GetBaseViewerDataQuery['matchHangingProtocolByWorklistsAndStudies']
): Nullable<DehydratedViewportsConfigurations> {
  return hangingProtocol?.viewportsConfigurations?.reduce((acc: any, { id, configuration }) => {
    return {
      ...acc,
      [id]: {
        ...configuration,
        viewType: validateViewType(configuration.viewType),
      },
    };
  }, {});
}

/**
 * Extracts layouts from a hanging protocol's `data.layouts` array.
 *
 * For stepped HPs the raw GQL response has `data.layouts: []` because each
 * step owns its own layouts. The `hangingProtocol` memo in `useHangingProtocol`
 * replaces `data.layouts` with the current step's layouts before this function
 * is called, so consumers always receive the correct value.
 */
export function getHangingProtocolLayouts(
  hangingProtocol: Nullable<
    GetBaseViewerDataQuery['matchHangingProtocolByWorklistsAndStudies'] | HangingProtocol
  >
): Nullable<Layouts> {
  if (hangingProtocol == null) return;

  return Object.fromEntries(
    hangingProtocol.data.layouts.map(
      ({ id, layout }: { id: string; layout: readonly number[] }) => [
        id,
        [layout[0], layout[1]], // converting the GraphQL array to a tuple
      ]
    )
  );
}
